// Rain audio, procedural like the car (no samples): a filtered-noise hiss for
// the rain itself, droplet patter on the car (pre-rendered loops of random
// ticks, light and dense, crossfaded by rain intensity), and wet-tyre hiss /
// spray rising with speed × standing water.
//
//   const rainAudio = new RainAudio(audioCtx);              // once, after the context is unlocked
//   rainAudio.update(rain, wetness, car.speed, frameDt);     // every frame
//   rainAudio.setMuted(true | false);                        // menus, hidden page
//
// It has its own output chain (default ctx.destination) and follows the master
// volume (audioParams.master). All nodes are made up front; update() only sets
// AudioParam targets. Levels sit under the engine: rain is ambience, not a lead.

import { makeRng, whiteNoise } from './mapping';
import { Smooth } from './engineVoice';
import { audioParams } from './params';

const clamp01 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 1 : Number.isFinite(v) ? v : 0);

/** Overall rain mix level before the master volume (car layers peak around 1). */
const RAIN_LEVEL = 0.32;

/**
 * A loop of random droplet ticks: short decaying tones (1.5–5 kHz, the band
 * phone speakers play well) with a little noise in the attack, plus the odd
 * heavier "plip" (a bigger drop on the halo or bodywork).
 */
export function patterBuffer(ctx: BaseAudioContext, seconds: number, ticksPerSecond: number, seed: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.round(seconds * sr);
  const data = new Float32Array(n);
  const rand = makeRng(seed);
  const ticks = Math.round(seconds * ticksPerSecond);
  for (let k = 0; k < ticks; k++) {
    const start = Math.floor(rand() * n);
    const big = rand() < 0.08;
    const amp = (0.25 + 0.75 * rand()) ** 2 * (big ? 1 : 0.7);
    const hz = big ? 700 + rand() * 900 : 1500 + rand() * 3500;
    const decay = (big ? 0.012 : 0.0025) * (0.6 + 0.8 * rand());
    const len = Math.min(n, Math.round(decay * 6 * sr));
    const w = (2 * Math.PI * hz) / sr;
    const phase = rand() * Math.PI * 2;
    for (let i = 0; i < len; i++) {
      const j = (start + i) % n; // wrap so the loop has no seam
      const t = i / sr;
      const env = Math.exp(-t / decay);
      const click = i < sr * 0.0006 ? (rand() * 2 - 1) * 0.5 : 0;
      data[j] = data[j]! + amp * env * (Math.sin(w * i + phase) + click);
    }
  }
  let peak = 0;
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(data[i]!));
  if (peak > 0) for (let i = 0; i < n; i++) data[i] = (data[i]! / peak) * 0.9;
  const buf = ctx.createBuffer(1, n, sr);
  buf.getChannelData(0).set(data);
  return buf;
}

export class RainAudio {
  private readonly owned: AudioNode[] = [];
  private readonly sources: AudioBufferSourceNode[] = [];
  private readonly master: Smooth;
  private readonly hiss: Smooth;
  private readonly hissLpHz: Smooth;
  private readonly body: Smooth;
  private readonly patterLight: Smooth;
  private readonly patterHeavy: Smooth;
  private readonly patterRateA: Smooth;
  private readonly patterRateB: Smooth;
  private readonly tyre: Smooth;
  private readonly tyreHz: Smooth;
  private readonly spray: Smooth;
  private readonly sprayHz: Smooth;
  private muted = false;
  private disposed = false;

  constructor(
    readonly ctx: BaseAudioContext,
    destination: AudioNode = ctx.destination,
  ) {
    const g = (v = 0): GainNode => {
      const n = ctx.createGain();
      n.gain.value = v;
      this.owned.push(n);
      return n;
    };
    const f = (type: BiquadFilterType, hz: number, q = 0.707): BiquadFilterNode => {
      const n = ctx.createBiquadFilter();
      n.type = type;
      n.frequency.value = hz;
      n.Q.value = q;
      this.owned.push(n);
      return n;
    };
    const loop = (buffer: AudioBuffer): AudioBufferSourceNode => {
      const s = ctx.createBufferSource();
      s.buffer = buffer;
      s.loop = true;
      s.start();
      this.sources.push(s);
      return s;
    };

    const sr = ctx.sampleRate;
    const noiseBuf = ctx.createBuffer(1, Math.round(sr * 2.3), sr);
    noiseBuf.getChannelData(0).set(whiteNoise(noiseBuf.length, 77));
    const white = loop(noiseBuf);

    // Output: bus → master (volume, mute) → destination.
    const out = g(0);
    out.connect(destination);
    this.master = new Smooth(out.gain, 0);
    const bus = g(RAIN_LEVEL);
    bus.connect(out);

    // Rain hiss: bright filtered noise, the "wall" of falling rain.
    const hissHp = f('highpass', 1800, 0.6);
    const hissLp = f('lowpass', 7000, 0.6);
    const hissGain = g(0);
    white.connect(hissHp).connect(hissLp).connect(hissGain).connect(bus);
    this.hiss = new Smooth(hissGain.gain, 0);
    this.hissLpHz = new Smooth(hissLp.frequency, 7000);

    // Body: a soft mid-band roar under heavy rain (kept above ~400 Hz for phone speakers).
    const bodyBand = f('bandpass', 650, 0.6);
    const bodyGain = g(0);
    white.connect(bodyBand).connect(bodyGain).connect(bus);
    this.body = new Smooth(bodyGain.gain, 0);

    // Droplets on the car: sparse loop and dense loop (different lengths so they never line up).
    const light = loop(patterBuffer(ctx, 3.3, 22, 11));
    const heavy = loop(patterBuffer(ctx, 2.7, 140, 23));
    const patterHp = f('highpass', 900, 0.7);
    const lightGain = g(0);
    const heavyGain = g(0);
    light.connect(lightGain).connect(patterHp);
    heavy.connect(heavyGain).connect(patterHp);
    patterHp.connect(bus);
    this.patterLight = new Smooth(lightGain.gain, 0);
    this.patterHeavy = new Smooth(heavyGain.gain, 0);
    this.patterRateA = new Smooth(light.playbackRate, 1);
    this.patterRateB = new Smooth(heavy.playbackRate, 1);

    // Wet tyres: a hissing "shhh" whose pitch rises with speed...
    const tyreBand = f('bandpass', 900, 0.8);
    const tyreGain = g(0);
    white.connect(tyreBand).connect(tyreGain).connect(bus);
    this.tyre = new Smooth(tyreGain.gain, 0);
    this.tyreHz = new Smooth(tyreBand.frequency, 900);
    // ...and the spray thrown into the wheel wells and floor: a darker swoosh.
    const sprayLp = f('lowpass', 500, 0.9);
    const sprayHp = f('highpass', 250, 0.7);
    const sprayGain = g(0);
    white.connect(sprayHp).connect(sprayLp).connect(sprayGain).connect(bus);
    this.spray = new Smooth(sprayGain.gain, 0);
    this.sprayHz = new Smooth(sprayLp.frequency, 500);
  }

  /** Call once per frame. `rain` and `wetness` are 0..1, `speed` in m/s. */
  update(rain: number, wetness: number, speed: number, dt: number): void {
    if (this.disposed) return;
    void dt; // levels glide with AudioParam time constants; kept for API symmetry with CarAudio
    const t = this.ctx.currentTime;
    const r = clamp01(rain);
    const w = clamp01(wetness);
    const v = Math.max(0, Number.isFinite(speed) ? speed : 0);
    const vk = clamp01(v / 70);
    const tau = 0.25;

    this.master.set(this.muted ? 0 : audioParams.master.value, t, this.muted ? 0.03 : 0.12);

    // Rain: hiss grows first, the roar and dense patter only in heavy rain.
    this.hiss.set(0.22 * r ** 0.7, t, tau);
    this.hissLpHz.set(5200 + 2800 * r, t, tau);
    this.body.set(0.5 * r * r, t, tau);
    // More drops hit the car the faster it goes.
    const hit = 1 + 0.8 * vk;
    this.patterLight.set(0.55 * Math.min(1, r * 3) * (1 - 0.6 * r) * hit, t, tau);
    this.patterHeavy.set(0.4 * r * r * hit, t, tau);
    this.patterRateA.set(1 + 0.25 * vk, t, 0.4);
    this.patterRateB.set(1 + 0.3 * vk, t, 0.4);

    // Tyres on standing water.
    const water = w * Math.min(1, v / 8) * (0.35 + 0.65 * vk);
    this.tyre.set(0.6 * water, t, 0.12);
    this.tyreHz.set(600 + 22 * v, t, 0.2);
    this.spray.set(1.2 * water * vk, t, 0.12);
    this.sprayHz.set(400 + 9 * v, t, 0.2);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.disposed) return;
    this.master.set(m ? 0 : audioParams.master.value, this.ctx.currentTime, 0.03);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
      s.disconnect();
    }
    for (const n of this.owned) n.disconnect();
  }
}
